/**
 * Country reference data , a compact list of every country a MediTalk
 * study has ever recruited from (or is likely to). For each entry we
 * store:
 *   - `code` , the ISO-3166 alpha-2 code (drives the emoji flag)
 *   - `name` , the canonical, capitalised display name
 *   - `aliases` , normalised lower-case strings that map to this entry
 *     when an admin's free-text country field is parsed (covers "USA",
 *     "United States", "U.S.A", "UK", "England", etc.)
 *
 * The emoji flag is generated on-the-fly from the country code via
 * codePointAt math , no PNG sprites, no CDN, no bundle weight.
 */
export const COUNTRIES = [
  { code: "GB", name: "United Kingdom", aliases: ["uk", "u.k.", "uk only", "united kingdom", "great britain", "britain", "england", "scotland", "wales", "northern ireland", "gb"] },
  { code: "US", name: "United States", aliases: ["us", "u.s.", "u.s.a", "u.s.a.", "usa", "united states", "united states of america", "america"] },
  { code: "CA", name: "Canada", aliases: ["canada", "ca"] },
  { code: "AU", name: "Australia", aliases: ["australia", "au", "aus"] },
  { code: "NZ", name: "New Zealand", aliases: ["new zealand", "nz"] },
  { code: "IE", name: "Ireland", aliases: ["ireland", "ie", "republic of ireland"] },
  { code: "FR", name: "France", aliases: ["france", "fr"] },
  { code: "DE", name: "Germany", aliases: ["germany", "de", "deutschland"] },
  { code: "IT", name: "Italy", aliases: ["italy", "it"] },
  { code: "ES", name: "Spain", aliases: ["spain", "es"] },
  { code: "PT", name: "Portugal", aliases: ["portugal", "pt"] },
  { code: "NL", name: "Netherlands", aliases: ["netherlands", "nl", "holland"] },
  { code: "BE", name: "Belgium", aliases: ["belgium", "be"] },
  { code: "CH", name: "Switzerland", aliases: ["switzerland", "ch"] },
  { code: "AT", name: "Austria", aliases: ["austria", "at"] },
  { code: "SE", name: "Sweden", aliases: ["sweden", "se"] },
  { code: "NO", name: "Norway", aliases: ["norway", "no"] },
  { code: "DK", name: "Denmark", aliases: ["denmark", "dk"] },
  { code: "FI", name: "Finland", aliases: ["finland", "fi"] },
  { code: "IS", name: "Iceland", aliases: ["iceland", "is"] },
  { code: "PL", name: "Poland", aliases: ["poland", "pl"] },
  { code: "CZ", name: "Czech Republic", aliases: ["czech republic", "czechia", "cz"] },
  { code: "GR", name: "Greece", aliases: ["greece", "gr"] },
  { code: "JP", name: "Japan", aliases: ["japan", "jp"] },
  { code: "KR", name: "South Korea", aliases: ["south korea", "korea", "kr"] },
  { code: "CN", name: "China", aliases: ["china", "cn"] },
  { code: "IN", name: "India", aliases: ["india", "in"] },
  { code: "SG", name: "Singapore", aliases: ["singapore", "sg"] },
  { code: "HK", name: "Hong Kong", aliases: ["hong kong", "hk"] },
  { code: "TW", name: "Taiwan", aliases: ["taiwan", "tw"] },
  { code: "BR", name: "Brazil", aliases: ["brazil", "br"] },
  { code: "MX", name: "Mexico", aliases: ["mexico", "mx"] },
  { code: "AR", name: "Argentina", aliases: ["argentina", "ar"] },
  { code: "ZA", name: "South Africa", aliases: ["south africa", "za"] },
  { code: "AE", name: "United Arab Emirates", aliases: ["united arab emirates", "uae", "ae"] },
  { code: "IL", name: "Israel", aliases: ["israel", "il"] },
];

const ALIAS_LOOKUP = (() => {
  const map = new Map();
  for (const c of COUNTRIES) {
    map.set(c.name.toLowerCase(), c);
    for (const a of c.aliases) map.set(a, c);
  }
  return map;
})();

/**
 * ISO-4217 currency + display symbol per country. Used to:
 *   - render the per-country compensation input with the correct
 *     currency-symbol prefix (admin types only the number),
 *   - auto-format the stored amount on study cards / modals /
 *     detail pages so the patient sees e.g. "£100" without the admin
 *     having to remember which symbol to type.
 *
 * Where multiple countries share a glyph (`$` is used by 6 countries)
 * the country flag + short name beside the amount disambiguates
 * cleanly , no need to clutter the symbol with region prefixes.
 */
export const CURRENCY_BY_COUNTRY = {
  // Major
  "United Kingdom": { code: "GBP", symbol: "£" },
  "United States": { code: "USD", symbol: "$" },
  Canada: { code: "CAD", symbol: "$" },
  Australia: { code: "AUD", symbol: "$" },
  "New Zealand": { code: "NZD", symbol: "$" },
  // Eurozone
  Ireland: { code: "EUR", symbol: "€" },
  France: { code: "EUR", symbol: "€" },
  Germany: { code: "EUR", symbol: "€" },
  Italy: { code: "EUR", symbol: "€" },
  Spain: { code: "EUR", symbol: "€" },
  Portugal: { code: "EUR", symbol: "€" },
  Netherlands: { code: "EUR", symbol: "€" },
  Belgium: { code: "EUR", symbol: "€" },
  Austria: { code: "EUR", symbol: "€" },
  Finland: { code: "EUR", symbol: "€" },
  Greece: { code: "EUR", symbol: "€" },
  // Nordic
  Sweden: { code: "SEK", symbol: "kr" },
  Norway: { code: "NOK", symbol: "kr" },
  Denmark: { code: "DKK", symbol: "kr" },
  Iceland: { code: "ISK", symbol: "kr" },
  // Rest of Europe
  Switzerland: { code: "CHF", symbol: "CHF " },
  Poland: { code: "PLN", symbol: "zł" },
  "Czech Republic": { code: "CZK", symbol: "Kč" },
  // Asia
  Japan: { code: "JPY", symbol: "¥" },
  "South Korea": { code: "KRW", symbol: "₩" },
  China: { code: "CNY", symbol: "¥" },
  India: { code: "INR", symbol: "₹" },
  Singapore: { code: "SGD", symbol: "$" },
  "Hong Kong": { code: "HKD", symbol: "$" },
  Taiwan: { code: "TWD", symbol: "$" },
  // Americas
  Brazil: { code: "BRL", symbol: "R$" },
  Mexico: { code: "MXN", symbol: "$" },
  Argentina: { code: "ARS", symbol: "$" },
  // Africa / Middle East
  "South Africa": { code: "ZAR", symbol: "R" },
  "United Arab Emirates": { code: "AED", symbol: "AED " },
  Israel: { code: "ILS", symbol: "₪" },
};

/**
 * Look up the currency for a country (alias-tolerant). Falls back to
 * `{ code: "", symbol: "" }` when we don't have a mapping , better
 * to render the raw amount than to throw.
 */
export function currencyFor(countryName) {
  if (!countryName) return { code: "", symbol: "" };
  const direct = CURRENCY_BY_COUNTRY[countryName];
  if (direct) return direct;
  const match = findCountry(countryName);
  if (match && CURRENCY_BY_COUNTRY[match.name]) {
    return CURRENCY_BY_COUNTRY[match.name];
  }
  return { code: "", symbol: "" };
}

// Symbols + ISO codes we recognise on the front of legacy free-text
// amounts , used by `formatAmountForCountry` to detect when the admin
// has already typed a symbol and to skip prepending it twice.
const KNOWN_SYMBOLS = ["£", "$", "€", "¥", "₹", "₩", "₪", "R$", "kr", "zł", "Kč", "CHF", "AED", "R", "NT$", "HK$", "S$", "C$", "A$", "NZ$", "Mex$", "AR$"];
const KNOWN_CODES = ["GBP", "USD", "CAD", "AUD", "NZD", "EUR", "SEK", "NOK", "DKK", "ISK", "CHF", "PLN", "CZK", "JPY", "KRW", "CNY", "INR", "SGD", "HKD", "TWD", "BRL", "MXN", "ARS", "ZAR", "AED", "ILS"];

/**
 * Render the compensation amount for a country in the canonical
 * display form. The admin form now only stores the raw number (e.g.
 * "100" or "100-200" or "up to 150"); this helper adds the symbol
 * appropriate to the country.
 *
 * Robust to legacy data:
 *   - "£100"          (leading symbol)         -> left alone
 *   - "$up to 60"     (admin pre-typed symbol) -> left alone
 *   - "100GBP"        (trailing ISO code)      -> stripped + reformatted
 *   - "100"           (bare amount)            -> "£100"
 */
export function formatAmountForCountry(countryName, rawAmount) {
  const trimmed = String(rawAmount || "").trim();
  if (!trimmed) return "";

  // Step 1 , strip a trailing ISO code like "100GBP" or "130 USD".
  // We only strip when the code is preceded by a digit or space so we
  // don't accidentally chop letters off a free-text description.
  let cleaned = trimmed;
  const upper = trimmed.toUpperCase();
  for (const c of KNOWN_CODES) {
    if (upper.endsWith(c) && upper.length > c.length) {
      const prev = upper[upper.length - c.length - 1];
      if (prev === " " || /\d/.test(prev)) {
        cleaned = trimmed.slice(0, -c.length).trimEnd();
        break;
      }
    }
  }

  // Step 2 , if the cleaned amount already contains an unambiguous
  // currency glyph anywhere in it, leave it alone (admin intentionally
  // typed "up to $60" or "around £100").
  const UNAMBIGUOUS = ["£", "$", "€", "¥", "₹", "₩", "₪"];
  if (UNAMBIGUOUS.some((sym) => cleaned.includes(sym))) return cleaned;

  // Step 3 , prepend the country's currency symbol.
  const { symbol, code } = currencyFor(countryName);
  if (symbol) {
    return symbol.endsWith(" ") ? `${symbol}${cleaned}` : `${symbol}${cleaned}`;
  }
  return code ? `${cleaned} ${code}` : cleaned;
}

/**
 * Inverse of formatAmountForCountry , when loading an existing study
 * into the admin form, strip the leading symbol (or trailing ISO
 * code) from the stored value so the input shows only the number the
 * admin would re-type. Idempotent and safe to call on already-bare
 * inputs.
 */
export function stripCurrencyPrefix(countryName, raw) {
  let s = String(raw || "").trim();
  if (!s) return "";
  const { symbol, code } = currencyFor(countryName);
  // Prefer the country's own symbol so "£100" -> "100" but "$130"
  // stored against the UK row (legacy bug) doesn't get corrupted.
  if (symbol && s.startsWith(symbol)) {
    s = s.slice(symbol.length).trimStart();
  } else {
    // Generic strip , any known symbol
    for (const sym of KNOWN_SYMBOLS) {
      if (s.startsWith(sym)) {
        s = s.slice(sym.length).trimStart();
        break;
      }
    }
  }
  // Trailing ISO code
  const upper = s.toUpperCase();
  if (code && upper.endsWith(code)) {
    s = s.slice(0, -code.length).trimEnd();
  } else {
    for (const c of KNOWN_CODES) {
      if (upper.endsWith(c)) {
        s = s.slice(0, -c.length).trimEnd();
        break;
      }
    }
  }
  return s;
}

/**
 * Emoji flag generated from an ISO-3166 alpha-2 country code.
 * "GB" → "🇬🇧", "US" → "🇺🇸". Returns "" for an unknown code.
 */
export function flagEmoji(code) {
  if (!code || code.length !== 2) return "";
  const A = 0x1f1e6;
  const upper = code.toUpperCase();
  return String.fromCodePoint(A + upper.charCodeAt(0) - 65) +
         String.fromCodePoint(A + upper.charCodeAt(1) - 65);
}

/** Look up a country by free-text input (alias-tolerant). Returns the
 *  COUNTRIES entry or `null` if no match.  */
export function findCountry(name) {
  if (!name) return null;
  return ALIAS_LOOKUP.get(String(name).trim().toLowerCase()) || null;
}

/**
 * Given a study, return a normalised array of country entries
 * (`[{code, name}]`). Handles three shapes:
 *   1. New shape , `study.countries` is already an array of names
 *   2. Legacy free-text , `study.country` is a single string. Splits
 *      on " and ", " & ", " + ", "," , and falls back to a single-
 *      element array if no split worked.
 *   3. Unknown , returns an empty array.
 *
 * Unknown / unparseable names are kept as `{code: null, name}` so the
 * UI still renders them (just without a flag).
 */
export function getStudyCountries(study) {
  if (!study) return [];
  const raw = Array.isArray(study.countries) && study.countries.length > 0
    ? study.countries
    : study.country
      ? splitLegacyCountryString(study.country)
      : [];
  return raw.map((name) => {
    const match = findCountry(name);
    return match
      ? { code: match.code, name: match.name }
      : { code: null, name: String(name).trim() };
  }).filter((c) => c.name);
}

function splitLegacyCountryString(raw) {
  return String(raw)
    .split(/\s+and\s+|\s*&\s*|\s*\+\s*|,/i)
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * Compose a compact human-friendly label for a list of countries:
 *  - 1 country  → "🇬🇧 United Kingdom"
 *  - 2 countries → "🇬🇧 🇺🇸 UK + US"
 *  - 3+        → "🇬🇧 🇺🇸 🇩🇪 3 countries"
 * Optional `mode` "full" forces the full name list regardless of count.
 */
export function formatCountryLabel(countries, { mode = "auto" } = {}) {
  if (!countries || countries.length === 0) return "";
  const flags = countries.map((c) => flagEmoji(c.code)).filter(Boolean).join(" ");
  if (mode === "full" || countries.length === 1) {
    return `${flags ? `${flags} ` : ""}${countries.map((c) => c.name).join(", ")}`;
  }
  if (countries.length === 2) {
    return `${flags ? `${flags} ` : ""}${countries.map((c) => shortName(c.name)).join(" + ")}`;
  }
  return `${flags ? `${flags} ` : ""}${countries.length} countries`;
}

const SHORT = {
  "United Kingdom": "UK",
  "United States": "US",
  "United Arab Emirates": "UAE",
  "South Korea": "S. Korea",
  "South Africa": "S. Africa",
  "New Zealand": "NZ",
  "Czech Republic": "Czechia",
};
export function shortName(name) {
  return SHORT[name] || name;
}

/**
 * Smart compensation picker. Given:
 *   - `study` (with `compensation` legacy field and/or
 *     `compensation_by_country` map)
 *   - `viewerCountry` (the country name the visitor cares about, or
 *     `null` if unknown)
 *
 * Returns an object `{ rows, fallback }` where:
 *   - `rows` is an array of `{ code, name, short, amount }` , one row
 *     per country , so the UI can render the stacked layout the
 *     product spec asks for (`🇬🇧 UK · £100` per line).
 *   - `fallback` is the legacy single-string compensation, used when
 *     `rows` is empty (the study has neither structured countries nor
 *     a compensation_by_country map).
 *
 * `viewerCountry` is no longer used to *hide* rows , the spec is now
 * "stack every country, transparent by default" , but kept in the
 * signature so we can prioritise the viewer's country to the top.
 */
export function selectCompensation(study, viewerCountry) {
  if (!study) return { rows: [], fallback: "" };
  const map = study.compensation_by_country;
  const hasMap = map && typeof map === "object" && Object.keys(map).length > 0;
  const countries = getStudyCountries(study);

  const rows = [];
  if (hasMap) {
    // Order: respect `countries` order (admin chose it) but bring the
    // viewer's country to the top when we know it.
    const ordered = [...countries];
    if (viewerCountry) {
      const idx = ordered.findIndex(
        (c) => c.name.toLowerCase() === viewerCountry.toLowerCase(),
      );
      if (idx > 0) {
        const [v] = ordered.splice(idx, 1);
        ordered.unshift(v);
      }
    }
    for (const c of ordered) {
      const rawAmount = map[c.name];
      if (!rawAmount) continue;
      rows.push({
        code: c.code,
        name: c.name,
        short: shortName(c.name),
        amount: formatAmountForCountry(c.name, rawAmount),
      });
    }
  } else if (countries.length === 1 && study.compensation) {
    // Single-country, no map: still display the stacked row with
    // flag + abbreviation + amount so the visual format is consistent
    // across all studies.
    const c = countries[0];
    rows.push({
      code: c.code,
      name: c.name,
      short: shortName(c.name),
      amount: formatAmountForCountry(c.name, study.compensation),
    });
  }

  return {
    rows,
    fallback: rows.length === 0 ? study.compensation || "" : "",
  };
}
